import { useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { COLLECTION_TYPES } from '@platform/contracts';
import { ChevronRight, Copy, Check } from 'lucide-react';
import { api } from '@/lib/api';
import { normalizeDatasetPage } from '@/lib/normalize';
import { usePagedQuery } from '@/lib/paged';
import { LoadMore } from '@/ui/load-more';
import type { DatasetSummary } from '@/lib/contract';
import { formatDay, formatHours, orDash, statusLabel, statusTone } from '@/lib/format';
import { acceptsUploads, commitVersion, filePath, planVersion, uploadFile } from '@/lib/upload';
import { canSubmit, useSubmitForEvaluation } from '@/lib/queries/dataset-submit';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { Button } from '@/ui/button';
import { Input } from '@/ui/input';
import { Badge } from '@/ui/badge';
import { MicroLabel } from '@/ui/micro-label';
import { Progress } from '@/ui/progress';
import { StatusDot } from '@/ui/status-dot';
import { DataTable, type Column } from '@/ui/data-table';
import { ErrorState } from '@/ui/error-state';
import { useVendorProfile } from '@/lib/queries/vendor-profile';
import { useRealtimeRefresh } from '@/lib/realtime';
import { GateBadge } from '@/domain/gate-badge';
import { QualityBlocks, QualityBlocksLegend } from '@/domain/quality-blocks';

const columns: Column<DatasetSummary>[] = [
  {
    key: 'name',
    header: 'Dataset',
    className: 'w-[200px]',
    render: row => <span className="font-mono text-[13px] text-foreground">{row.name}</span>
  },
  {
    key: 'hours',
    header: 'Hours',
    className: 'w-[70px]',
    render: row => <span className="font-mono text-[13px]">{formatHours(row.hours)}</span>
  },
  {
    key: 'format',
    header: 'Format',
    className: 'w-[120px]',
    render: row => (
      <MicroLabel tone="muted" size="xs" caps={false}>
        {orDash(row.format_label)}
      </MicroLabel>
    )
  },
  {
    key: 'method',
    header: 'Method',
    className: 'w-[100px]',
    render: row => (
      <MicroLabel tone="muted" size="xs">
        {orDash(row.intake_method)}
      </MicroLabel>
    )
  },
  {
    key: 'submitted',
    header: 'Submitted',
    className: 'w-[100px]',
    render: row => (
      <MicroLabel tone="muted" size="xs" caps={false}>
        {formatDay(row.submitted_at)}
      </MicroLabel>
    )
  },
  {
    key: 'stage',
    header: 'Stage',
    className: 'w-[140px]',
    render: row => <StatusDot tone={statusTone(row.lifecycle_status)} label={statusLabel(row.lifecycle_status)} />
  },
  {
    key: 'gate',
    header: 'Gate',
    className: 'w-[70px]',
    render: row => <GateBadge gate={row.gate} />
  },
  {
    key: 'quality',
    header: 'Quality',
    className: 'w-[130px]',
    render: row => <QualityBlocks blocks={row.quality_blocks} size="sm" />
  },
  {
    key: 'flags',
    header: 'Flags',
    className: 'w-[80px]',
    render: row =>
      row.open_flag_count > 0 ? (
        <MicroLabel tone="danger" size="xs">
          {row.open_flag_count} open
        </MicroLabel>
      ) : (
        <span className="text-dim">{row.gate ? '0' : '—'}</span>
      )
  },
  {
    key: 'chevron',
    header: '',
    className: 'w-8 text-right',
    render: () => <ChevronRight className="ml-auto size-3.5 text-dim" />
  }
];

/**
 * Committing a batch to QA is its own act, and a deliberate one: a run is billed, and only the
 * server knows when the files behind it have passed their checks. So the button appears exactly
 * when `ready_for_evaluation` says the admission gates would let it through, and at no other
 * stage the server can report.
 *
 * It used to fire automatically as the last byte landed. Preflight is asynchronous -- outbox,
 * Debezium, Kafka, the ancillary lane -- so that submit almost always arrived while the checks
 * were still running, came back 409 `checks_in_progress`, and was rendered as "Upload failed."
 * The upload had in fact succeeded, and there was no way to try again.
 */
function submitColumn(onSubmit: (row: DatasetSummary) => void, pendingId: string | null): Column<DatasetSummary> {
  return {
    key: 'submit',
    header: '',
    // Sized to its content, not pinned. At 150px the label was wider than its own cell, so it
    // spilled past the table edge and the wrapper's `overflow-hidden` sliced it to "Submit for ev".
    // `w-[1%]` is the table idiom for "take exactly what you need": the flexible columns absorb the
    // remainder.
    className: 'w-[1%] whitespace-nowrap',
    render: row =>
      canSubmit(row.lifecycle_status) ? (
        <Button
          size="sm"
          variant="secondary"
          disabled={pendingId === row.id}
          onClick={event => {
            // The row itself opens the detail view; the button does not.
            event.stopPropagation();
            onSubmit(row);
          }}
          // Two lines, so the widest column in this table is not a button label. `Button` is
          // `whitespace-nowrap` by default, which is right for every other button in the app --
          // overridden here rather than changed there.
          // Fixed in both dimensions so "Submitting..." and the two-line idle label occupy the
          // same box. The placeholder below keeps that box after the action disappears; otherwise
          // the auto-layout table recomputes every column and shrinks the row in the same frame.
          className="h-[42px] w-[104px] flex-col gap-0 py-1.5 leading-tight whitespace-normal"
        >
          {pendingId === row.id ? (
            'Submitting…'
          ) : (
            <>
              <span>Submit for</span>
              <span>evaluation</span>
            </>
          )}
        </Button>
      ) : (
        <span aria-hidden="true" className="block h-[42px] w-[104px]" />
      )
  };
}

export function VendorSubmissions() {
  // A batch has to exist before anything can be uploaded into it -- the upload endpoints are all
  // scoped to a dataset id. Registering one is therefore the first step of the flow, not a detail.
  const [registering, setRegistering] = useState(false);
  /**
   * A *preference*, not the destination. The destination is derived below, so it can never name a
   * batch that has stopped accepting uploads.
   *
   * This used to hold the destination itself, written only by the register form and cleared after
   * every successful upload. Batches registered in an earlier session, or before a reload, or a
   * minute ago, all left it null -- so the panel asked the vendor to "register a batch first" while
   * the table above listed the batches they already had.
   */
  const [targetId, setTargetId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  const page = usePagedQuery({
    queryKey: ['vendor', 'datasets'],
    fetchPage: async cursor =>
      normalizeDatasetPage(await api(`/datasets${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`)),
    // Realtime now pushes the same hint, so this is a fallback rather than the mechanism: it only
    // runs while something is actually moving, and at a much longer interval than the 5s it used
    // to need when polling was the only thing keeping the table current.
    refetchInterval: rows => {
      const moving = rows.some(r => ['uploading', 'evaluating', 'ready_for_evaluation'].includes(r.lifecycle_status));
      return moving ? 30000 : false;
    }
  });
  const { isPending, isError, error, refetch } = page;

  /**
   * The batches an upload can still go into, and the one it will.
   *
   * `submitted_at` and `terminal_outcome` are exactly the two fields the server checks before
   * refusing an upload with "Files cannot be added after evaluation has started", so asking them
   * here costs no request and cannot disagree with the API. The list is already newest-first
   * (`ORDER BY f.created_at DESC`), so the head of it is the batch a vendor most likely means.
   */
  const openBatches = useMemo(() => page.rows.filter(acceptsUploads), [page.rows]);
  const target = openBatches.find(batch => batch.id === targetId) ?? openBatches[0] ?? null;

  // `datasets:tenant.<t>.stream` — every pipeline stage for this tenant, not just the verdict.
  useRealtimeRefresh(
    '/datasets/subscription-token',
    [
      ['vendor', 'datasets'],
      ['vendor', 'dashboard']
    ],
    {
      realm: 'vendor'
    }
  );

  // Shared with the detail screen, which offers the same action -- see lib/queries/dataset-submit.ts.
  const submit = useSubmitForEvaluation({ onError: setSubmitError });

  const tableColumns = useMemo(
    () => [
      ...columns.slice(0, -1),
      submitColumn(row => submit.mutate(row.id), submit.isPending ? (submit.variables ?? null) : null),
      columns[columns.length - 1]
    ],
    [submit.isPending, submit.variables]
  );

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Submissions"
        subtitle="Manifest registration default · browser upload for samples ≤ 5 h"
        actions={<Button onClick={() => setRegistering(v => !v)}>{registering ? 'Cancel' : '+ Register batch'}</Button>}
      />

      {registering ? (
        <RegisterBatch
          onRegistered={batch => {
            setTargetId(batch.id);
            setRegistering(false);
          }}
        />
      ) : null}

      {submitError ? (
        <p className="rounded border border-danger/40 bg-card px-4 py-3 text-[13px] text-danger" role="alert">
          {submitError}
        </p>
      ) : null}

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load your submissions" />
      ) : (
        <>
          <DataTable
            columns={tableColumns}
            rows={page.rows}
            rowKey={row => row.id}
            onRowClick={row => navigate(`/v/submissions/${row.id}`)}
            empty={isPending ? 'Loading…' : 'No batches registered yet.'}
            footer={<QualityBlocksLegend />}
          />
          {/* No totals on the vendor listing, so this reports what is loaded and whether more
              exist -- which is the part that was missing, not the grand total. */}
          <LoadMore
            shown={page.rows.length}
            hasMore={page.hasMore}
            loading={page.loadingMore}
            onLoadMore={page.loadMore}
          />
        </>
      )}

      <div className="grid grid-cols-2 gap-5 max-lg:grid-cols-1">
        <IntakePanel />
        {/* The destination survives the upload. Clearing it here is what used to send the panel
            back to its empty state the moment a vendor succeeded at using it. */}
        <BrowserUploadPanel batches={openBatches} target={target} onSelect={setTargetId} />
      </div>
    </div>
  );
}

/**
 * What a vendor declares at intake: the things no amount of reading the bytes can establish.
 *
 * Hours is not among them, and used to be. It was declared here and *supposed* to be superseded by
 * what the pipeline measured — but only LeRobot uploads were ever measured, so for every other
 * shape the declaration was what the platform showed, summed into the dashboard total, and billed
 * against. A number nobody checks does not belong on a form.
 */
function RegisterBatch({ onRegistered }: { onRegistered: (batch: { id: string; name: string }) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [collection, setCollection] = useState('');
  const [rig, setRig] = useState('');

  const register = useMutation({
    mutationFn: () =>
      api<{ id: string; name: string; dataset_code: string | null }>('/datasets', {
        method: 'POST',
        body: JSON.stringify({
          name,
          collection_type: collection || null,
          rig: rig || null
        })
      }),
    onSuccess: batch => {
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'datasets'] });
      onRegistered({ id: batch.id, name: batch.dataset_code ?? batch.name });
    }
  });

  return (
    <Panel title="Register batch" subtitle="Hours are measured from the upload, not declared">
      <form
        className="grid grid-cols-3 items-end gap-3 max-lg:grid-cols-2"
        onSubmit={event => {
          event.preventDefault();
          register.mutate();
        }}
      >
        <label className="grid gap-1">
          <MicroLabel size="xs">Dataset</MicroLabel>
          <Input
            required
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="ego-office-045"
            className="font-mono text-[12px]"
          />
        </label>
        <label className="grid gap-1">
          <MicroLabel size="xs">Collection</MicroLabel>
          <select
            value={collection}
            onChange={e => setCollection(e.target.value)}
            className="h-9 cursor-pointer rounded border border-border bg-inset px-2 text-[13px] text-foreground"
          >
            <option value="">—</option>
            {COLLECTION_TYPES.map(t => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1">
          <MicroLabel size="xs">Rig</MicroLabel>
          <Input
            value={rig}
            onChange={e => setRig(e.target.value)}
            placeholder="Aria Gen 2"
            className="font-mono text-[12px]"
          />
        </label>
        <div className="col-span-4 flex items-center gap-3 max-lg:col-span-2">
          <Button type="submit" disabled={register.isPending || !name}>
            {register.isPending ? 'Registering…' : 'Register'}
          </Button>
          {register.isError ? (
            <span className="text-[12px] text-danger">
              {register.error instanceof Error ? register.error.message : 'Could not register the batch.'}
            </span>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}

function IntakePanel() {
  const [copied, setCopied] = useState(false);
  const { data } = useVendorProfile();
  const bucket = data?.intake?.bucket ?? '';

  function copy() {
    navigator.clipboard.writeText(bucket).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <Panel title="Manifest intake" action={<Badge>Default</Badge>}>
      <p className="text-[13px] leading-relaxed text-muted">
        Drop a manifest json into your intake prefix. We pull the data, verify checksums, and register the batch
        automatically.
      </p>
      <div className="mt-4 flex items-center gap-2">
        <Input readOnly value={bucket} placeholder="—" className="font-mono text-[12px]" />
        <Button variant="secondary" onClick={copy} className="shrink-0">
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </Panel>
  );
}

/**
 * Browser upload. This is the one place the client owns state the server does not: in-flight upload
 * progress (frontend.md section 1).
 *
 * It uploads into a registered batch and stops there. Until a batch is selected there is no dataset
 * id to upload into, and the button does nothing rather than pretending to work.
 *
 * It does **not** submit. Preflight runs after the bytes land, and only the server can say when the
 * files have passed; the table's Submit action appears when they have.
 */
function BrowserUploadPanel({
  batches,
  target,
  onSelect
}: {
  batches: DatasetSummary[];
  target: DatasetSummary | null;
  onSelect: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const folderRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [stage, setStage] = useState('');
  // What the last plan resolved to, so the panel can report a delta rather than just "uploaded".
  const [version, setVersion] = useState<number | null>(null);
  const [carried, setCarried] = useState(0);
  const [removed, setRemoved] = useState(0);
  /**
   * What did not get through, and enough to try it again.
   *
   * One part of one file failing used to reject out of the loop below and abandon every file after
   * it -- a 26-file batch stopped at file 14 with no record of what had succeeded and no way back
   * except re-picking the folder. Holding the failures, and the `File` handles behind them, is what
   * turns that into something the vendor can act on.
   */
  const [failed, setFailed] = useState<{ file: File; reason: string }[]>([]);
  /**
   * The file being sent right now, so the panel has something to say while the batch bar does not
   * move. One 200 MB video is most of a small batch, and for the minutes it takes, a batch bar sits
   * still -- which looks exactly like a hung upload. This is the second line: which file, where it
   * is in the queue, and which part is in flight.
   */
  const [current, setCurrent] = useState<{
    name: string;
    index: number;
    total: number;
    fraction: number;
    parts: { completed: number; total: number } | null;
  } | null>(null);

  async function onFiles(files: FileList | null) {
    if (!target) return;
    const chosen = Array.from(files ?? []);
    if (!chosen.length) return;
    setError('');
    setStage('hashing');
    setProgress(0);
    try {
      /**
       * Plan first, then send only what is missing.
       *
       * The vendor re-selects the whole folder — that is the only thing they can reliably do — and
       * the diff happens here rather than in their head. A remediation of a LeRobot dataset is two
       * changed files out of three hundred, and this is what makes the other two hundred and
       * ninety-eight cost nothing. It also opens the next version when the current one is frozen,
       * so correcting a rejected batch needs no separate action.
       */
      const plan = await planVersion(target.id, chosen);
      const needed = new Set(plan.need.map(n => n.path));
      const upload = chosen.filter(file => needed.has(filePath(file)));
      setVersion(plan.package_version);
      setCarried(chosen.length - upload.length);
      setRemoved(plan.deletes.length);
      setStage('uploading');

      /**
       * Sequential on purpose: upload.ts already parallelises the parts of one file, and a browser
       * sample is a handful of files, so a second layer of concurrency buys nothing and makes the
       * progress figure meaningless.
       *
       * Each file's failure is its own. `uploadFile` already retries a part the store was merely
       * busy with, so reaching this catch means the file genuinely did not make it -- and that is a
       * reason to report it, not a reason to abandon the twelve files behind it in the queue.
       */
      const failures: { file: File; reason: string }[] = [];
      for (let i = 0; i < upload.length; i++) {
        const file = upload[i];
        setFileName(file.name);
        setCurrent({ name: file.name, index: i + 1, total: upload.length, fraction: 0, parts: null });
        try {
          await uploadFile(target.id, file, {
            onProgress: (fraction, parts) => {
              setProgress((i + fraction) / upload.length);
              setCurrent(c => (c ? { ...c, fraction, parts: parts ?? null } : c));
            },
            // A directory drop carries the path, which is what lets the backend recognise
            // meta/info.json and read the dataset's own description of itself.
            relativePath: (file as File & { webkitRelativePath?: string }).webkitRelativePath || undefined
          });
        } catch (cause) {
          failures.push({ file, reason: cause instanceof Error ? cause.message : 'Upload failed.' });
        }
      }
      setFailed(failures);
      // The absences: files the previous version held that this selection does not. Nothing arriving
      // can express a deletion, so it is stated rather than inferred.
      if (plan.deletes.length) await commitVersion(target.id, plan.deletes);
      // Back to idle, not to a full bar. `progress` is what disables the panel, so leaving it at 1
      // locked the dropzone, both pickers and the batch selector until a reload -- succeeding at an
      // upload was the one way to make the panel unusable. The stage line below is what reports the
      // outcome; a bar pinned at 100% only says "still busy".
      setProgress(null);
      setCurrent(null);
      setFileName('');
      setStage(failures.length ? 'partial' : 'uploaded');
      if (failures.length)
        setError(
          `${failures.length} of ${upload.length} ${failures.length === 1 ? 'file' : 'files'} did not upload. ${failures[0].reason}`
        );
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'datasets'] });
    } catch (cause) {
      // Everything outside the per-file loop: planning, hashing, the deletes commit. A failure here
      // is about the batch rather than about one file, so there is nothing partial to report.
      setError(cause instanceof Error ? cause.message : 'Upload failed.');
      setProgress(null);
      setCurrent(null);
      setStage('');
    }
  }

  /** Re-send only what failed, without making the vendor find the folder again. */
  async function retryFailed() {
    const files = failed.map(f => f.file);
    if (!files.length) return;
    setFailed([]);
    await onFiles(files as unknown as FileList);
  }

  const STAGE_LABEL: Record<string, string> = {
    // Hashing happens before a byte moves, and on a large folder it is the longest visible pause.
    // Saying so beats a progress bar that sits at zero.
    hashing: 'Checking which files changed…',
    uploading: `Uploading ${fileName}`,
    // Where the upload ends. What happens next -- checks, then a submit -- belongs to the server
    // and to the vendor respectively, and the table above reports both.
    uploaded: [
      version ? `v${version} ·` : null,
      carried ? `${carried} unchanged ${carried === 1 ? 'file' : 'files'} carried forward ·` : null,
      // Said out loud. A folder re-selection removes what it no longer contains, and a vendor who
      // is not told that has to discover it by counting rows on another screen.
      removed ? `${removed} no longer in the folder removed ·` : null,
      'checks run before it can be submitted'
    ]
      .filter(Boolean)
      .join(' '),
    // Some of it landed. Saying "uploaded" would be a lie, and saying nothing would leave the
    // vendor to work out which of twenty-six files is missing.
    partial: [
      version ? `v${version} ·` : null,
      `${failed.length} ${failed.length === 1 ? 'file' : 'files'} still to upload`
    ]
      .filter(Boolean)
      .join(' ')
  };

  const busy = progress !== null;
  return (
    <Panel title="Browser upload" action={<Badge>Samples ≤ 5 h</Badge>}>
      <p className="text-[13px] leading-relaxed text-muted">
        For engagement samples and small test batches only. Larger batches must go through manifest intake.
      </p>

      {target ? (
        <label className="mt-4 flex items-center gap-2.5 text-[12px] text-muted">
          <span className="font-mono text-[11px] tracking-[0.08em] uppercase">Into</span>
          <select
            value={target.id}
            disabled={busy}
            onChange={event => onSelect(event.target.value)}
            className="flex-1 rounded border border-border bg-inset px-2.5 py-1.5 text-[13px] text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
          >
            {batches.map(batch => (
              <option key={batch.id} value={batch.id}>
                {batch.name} · {statusLabel(batch.lifecycle_status)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        // The genuine empty state, and now the only one: there is no batch left open to upload into.
        <p className="mt-3 text-[12px] leading-relaxed text-dim">
          Register a batch first — an upload has to go somewhere, and the destination is what the evaluation runs
          against.
        </p>
      )}

      {/*
        Why a batch will not move, and what actually clears it.

        `needs_fixes` has two quite different causes and this used to state only the first: "a file
        did not pass its checks... until that file is removed". That is the *intake* verdict. Once a
        version has been submitted the far more common cause is the opposite -- every file passed
        intake and the evaluation rejected what was inside them -- and there the advice was not
        merely imprecise but impossible, because a submitted version is frozen and refuses removals.

        `submitted_at` separates them, and it is the same field the server freezes on.
      */}
      {target?.lifecycle_status === 'needs_fixes' ? (
        <p className="mt-3 text-[12px] leading-relaxed text-danger">
          {target.submitted_at ? (
            <>
              The evaluation rejected v{target.package_version}. Uploading a corrected folder starts v
              {target.package_version + 1} — files you have not changed are carried over.{' '}
            </>
          ) : (
            <>A file here did not pass its intake checks, so this batch cannot be submitted until it is replaced. </>
          )}
          <Link to={`/v/submissions/${target.id}`} className="underline underline-offset-2">
            Open {target.name}
          </Link>{' '}
          to see which files.
        </p>
      ) : null}

      {stage ? (
        <p className="mt-3 font-mono text-[11px] tracking-[0.08em] text-muted uppercase">
          {STAGE_LABEL[stage] ?? stage}
        </p>
      ) : null}
      <button
        type="button"
        disabled={!target || busy}
        onClick={() => folderRef.current?.click()}
        onDragOver={event => event.preventDefault()}
        onDrop={event => {
          event.preventDefault();
          void onFiles(event.dataTransfer.files);
        }}
        className="mt-4 w-full rounded border border-dashed border-border-strong px-4 py-4 text-[13px] text-muted transition-colors enabled:cursor-pointer enabled:hover:border-primary enabled:hover:text-foreground disabled:opacity-50"
      >
        {fileName || (target ? 'Drop a folder or an archive here' : 'Register a batch first')}
      </button>
      {/*
        Two pickers, because a vendor arrives with one of two shapes and a single input cannot offer
        both: `webkitdirectory` opens a *folder* chooser, which cannot select a file at all. So an
        archive -- the other thing intake accepts -- was unreachable except by dragging it in.
      */}
      <div className="mt-2 flex gap-2">
        <Button variant="secondary" size="sm" disabled={!target || busy} onClick={() => folderRef.current?.click()}>
          Browse folder
        </Button>
        <Button variant="secondary" size="sm" disabled={!target || busy} onClick={() => fileRef.current?.click()}>
          Browse files
        </Button>
      </div>
      {/*
        A LeRobot dataset is a directory, and its structure is the thing worth reading -- meta/
        info.json is what states the fps, the totals and the feature map. So this picker takes a
        folder, and webkitRelativePath is what carries that structure to the server.
      */}
      <input
        ref={folderRef}
        type="file"
        hidden
        multiple
        // @ts-expect-error directory pickers are not in the React DOM typings
        webkitdirectory=""
        onChange={event => void onFiles(event.target.files)}
      />
      <input ref={fileRef} type="file" hidden multiple onChange={event => void onFiles(event.target.files)} />
      {/*
        Two bars, because they answer different questions. The batch bar says how much of the
        selection is done; on a folder whose weight is one 200 MB video it can sit still for minutes,
        and a still bar is indistinguishable from a dead one. The file bar underneath is what moves.

        Parts rather than a smoothed percentage: `fetch` reports nothing while a request body is in
        flight, so the only honest events are part completions. Naming them means a pause reads as
        "part 7 is still going" instead of "nothing is happening". Single-part files just show a
        percentage -- "0 of 1 parts" tells no one anything.
      */}
      {progress !== null ? (
        <div className="mt-3 grid gap-2.5">
          <div className="grid gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <MicroLabel size="xs">Batch</MicroLabel>
              {current ? (
                <span className="font-mono text-[11px] text-muted">
                  file {current.index} of {current.total}
                </span>
              ) : null}
            </div>
            <Progress value={progress} />
          </div>
          {current ? (
            <div className="grid gap-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate font-mono text-[11px] text-muted">{current.name}</span>
                <span className="shrink-0 font-mono text-[11px] text-dim">
                  {current.parts && current.parts.total > 1
                    ? `${current.parts.completed} of ${current.parts.total} parts · `
                    : ''}
                  {Math.round(current.fraction * 100)}%
                </span>
              </div>
              <Progress value={current.fraction} tone="muted" />
            </div>
          ) : null}
        </div>
      ) : null}
      {error ? <p className="mt-2 text-[12px] text-danger">{error}</p> : null}
      {/*
        The files that did not make it, and one button to send just those. `uploadFile` has already
        retried anything the store was merely busy with, so these are real failures -- but a real
        failure on one file is no reason to make the vendor re-pick a folder of three hundred.
      */}
      {failed.length ? (
        <div className="mt-3 grid gap-2 rounded border border-danger/40 bg-inset px-3 py-3">
          <MicroLabel size="xs" tone="danger">
            Not uploaded
          </MicroLabel>
          <ul className="grid gap-1">
            {failed.slice(0, 5).map(f => (
              <li key={f.file.name} className="font-mono text-[11px] text-muted">
                {f.file.name}
              </li>
            ))}
            {failed.length > 5 ? (
              <li className="font-mono text-[11px] text-dim">and {failed.length - 5} more</li>
            ) : null}
          </ul>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => void retryFailed()}>
            {busy ? 'Uploading…' : `Retry ${failed.length} ${failed.length === 1 ? 'file' : 'files'}`}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}
